import _ from 'lodash';

import state from './listingState';
import { getters, mutations, actions } from './listing.js';
import ColumnDefinition from 'vcomponents/listing/apiListing/ColumnDefinition.js';
import { FilterTranslator } from 'vcomponents/listing/filters/FilterTranslator';
import BatchActionDefinition from 'vcomponents/listing/apiListing/BatchActionDefinition.js';

export default {
    namespaced: true,
    state() {
        return JSON.parse(JSON.stringify(state));
    },
    getters: {
        ...getters,

        formattedFilters(state, getters) {
            var filters = {};

            //Call filter adapter
            const filtersData = getters.filterData;
            for (const filterName in filtersData) {
                if (state.filterDefinitions[filterName]) {
                    filters[filterName] = state.filterDefinitions[filterName].dataAdapter(
                        filtersData[filterName],
                    );
                } else {
                    filters[filterName] = filtersData[filterName];
                }
            }

            return filters;
        },

        mustGetKpis(state) {
            return (
                state.listingMapping.kpis !== undefined &&
                state.listingMapping.kpis.length > 0 &&
                !state.hasKpis
            );
        },
    },
    mutations: {
        ...mutations,
        init(state, props) {
            mutations.init(state, props);
            state.listingMapping = props.listingMapping;
            state.sorting = props.listingMapping.defaultSort
                ? props.listingMapping.defaultSort
                : { field: '', direction: 'asc' };

            if (state.url === null) {
                throw new Error('Listing url is missing');
            }
        },
        updateFilterDefinitions(state, definitions) {
            state.filterDefinitions = definitions;
        },
        updateMetadatas(state, metas) {
            state.metadatas = typeof metas === 'object' ? metas : {};
        },
        activePendingFilters(state) {
            state.filters = state.filters.map(function (filter) {
                if (filter.pending) {
                    filter.pending = false;
                    filter.active = true;
                }
                return filter;
            });
        },
        applyFilter(state, filter) {
            mutations.applyFilter(state, filter);
            state.hasKpis = false;
        },

        removeFilter(state, filter) {
            mutations.removeFilter(state, filter);
            state.hasKpis = false;
        },
    },
    actions: {
        ...actions,
        async init({ dispatch, commit }, props) {
            actions.init({ dispatch, commit }, props);
            await dispatch('loadMeta');
            await dispatch('loadColumns');
            await dispatch('loadFilters');
            await dispatch('loadContextMenu');
            await dispatch('loadBatchActions');
            await dispatch('loadUserContext');
        },

        loadMeta({ state, commit }) {
            if (state.metaUrl === null) {
                return Promise.resolve();
            }
            return new Promise((resolve, reject) => {
                commit(
                    'setAjax',
                    ApiClient.get(state.metaUrl)
                        .then(function (response) {
                            try {
                                let metas = response.data;
                                if (typeof state.listingMapping.hooks.metas === 'function') {
                                    metas = state.listingMapping.hooks.metas(metas);
                                }

                                if (typeof metas.permissions === 'object') {
                                    commit(
                                        'updatePermissions',
                                        Object.assign(state.permissions, metas.permissions),
                                    );
                                } else if (typeof metas === 'object'){
                                    commit(
                                        'updatePermissions',
                                        Object.assign(state.permissions, metas),
                                    );
                                }

                                commit('updateMetadatas', metas);
                                resolve(metas);
                            } catch (e) {
                                reject(e);
                            }
                        })
                        .catch(function () {
                            reject();
                            commit('setAjax', false);
                        }),
                );
            });
        },

        loadUserContext({ state, commit }) {
            if (!state.enableContext) {
                return Promise.resolve();
            } else {
                return new Promise((resolve, reject) => {
                    commit(
                        'setAjax',
                        ApiClient.get(`/listings/${state.listingname}/context/me`)
                            .then(function (response) {
                                const userContext = response.data;
                                if (
                                    userContext.sorting &&
                                    userContext.sorting.field &&
                                    userContext.sorting.direction
                                ) {
                                    commit('updateSorting', userContext.sorting);
                                }
                                if (userContext.pagination && userContext.pagination.current_page) {
                                    commit(
                                        'updateCurrentPage',
                                        userContext.pagination.current_page,
                                    );
                                }
                                if (userContext.pagination && userContext.pagination.per_page) {
                                    commit('updatePerPage', userContext.pagination.per_page);
                                }
                                if (userContext.columns && userContext.columns.length > 0) {
                                    state.columns.forEach(function (column) {
                                        const rank = userContext.columns.indexOf(column.name);
                                        if (rank == -1) {
                                            //Hide column
                                            column.display = column.isMain;
                                        } else {
                                            column.display = true;
                                            column.rank = rank;
                                        }
                                    });
                                    commit('updateColumns', state.columns);
                                }
                                if (
                                    userContext.columns_size &&
                                    userContext.columns_size.length > 0
                                ) {
                                    commit('updateColumnsSize', userContext.columns_size);
                                }
                                if (
                                    userContext.filters &&
                                    Object.keys(userContext.filters).length > 0
                                ) {
                                    const filters = state.filters.map(function (filter) {
                                        if (userContext.filters.hasOwnProperty(filter.name)) {
                                            filter.data = userContext.filters[filter.name];
                                            filter.pending = true;
                                            filter.translation = new FilterTranslator(
                                                filter,
                                            ).translate();
                                        }
                                        return filter;
                                    });
                                    commit('updateFilters', filters);
                                }
                                if (userContext.display) {
                                    commit('updateDisplay', userContext.display);
                                }
                                resolve(response);
                            })
                            .catch(function () {
                                reject();
                                commit('setAjax', false);
                            }),
                    );
                });
            }
        },

        saveUserContext: _.debounce(({ state, commit, getters }) => {
            return new Promise((resolve, reject) => {
                const data = {
                    sorting: null,
                    pagination: null,
                    filters: getters.filterData,
                    columns: getters.activeColumns.map((col) => col.name),
                    columns_size: null,
                };

                if (state.sorting.field && state.sorting.direction) {
                    data.sorting = {
                        field: state.sorting.field,
                        direction: state.sorting.direction.toLowerCase(),
                    };
                }

                if (state.pagination) {
                    data.pagination = {
                        current_page: state.pagination.current_page,
                        per_page: state.pagination.per_page,
                    };
                }

                if (
                    state.columnsSize[state.listingname] &&
                    state.columnsSize[state.listingname].length > 0
                ) {
                    data.columns_size = state.columnsSize[state.listingname];
                }

                if (state.display) {
                    data.display = _.pick(state.display, ['filters', 'density', 'kpi']);
                }

                ApiClient.put(`/listings/${state.listingname}/context/me`, {}, data)
                    .then(function (response) {
                        commit('setAjax', false);
                        resolve(response);
                    })
                    .catch(function () {
                        reject();
                        commit('setAjax', false);
                    });
            });
        }, 500),

        loadColumns({ state, commit }) {
            const columns = state.listingMapping.columns.map(function (column) {
                if (!column instanceof ColumnDefinition) {
                    throw new Error('The column does not instance of ColumnDefinition');
                }
                column.metas = state.metadatas;
                return column.build();
            });
            commit('updateColumns', columns);
        },

        loadFilters({ state, commit }) {
            const definitions = {};
            const filters = state.listingMapping.filters
                .map(function (filter) {
                    if (
                        typeof filter.build !== 'function' ||
                        typeof filter.dataAdapter !== 'function'
                    ) {
                        throw new Error('The filter does not extend AbstractFilterDefinition');
                    }
                    filter.metas = state.metadatas;
                    definitions[filter.name] = filter;
                    return filter.build();
                })
                .filter((filter) => filter.inputs !== null);

            commit('updateFilters', filters);
            commit('updateFilterDefinitions', definitions);
        },

        loadBatchActions({ state, commit }) {
            const actions = state.listingMapping.batchActions.map(function (action) {
                if (!action instanceof BatchActionDefinition) {
                    throw new Error('The action does not instance of BatchActionDefinition');
                }

                action.metas = state.metadatas;
                return action.build();
            });
            commit('updateBatchActions', actions);
        },

        loadContextMenu({ state }) {
            state.listingMapping.contextMenus.forEach(function (menu) {
                menu.metas = state.metadatas;
            });
        },

        updateDisplay({ dispatch }) {
            dispatch('saveUserContext');
        },

        updateColumns({ dispatch }) {
            dispatch('saveUserContext');
        },

        updateColumnsSettings({ dispatch, getters, state, commit }, meta) {
            commit('updateColumnSize', { name: meta.column.property, width: meta.newWidth });
            dispatch('saveUserContext');
        },

        formatPagination({ dispatch, state, commit }, pagination) {
            let currentPage = 1;
            let lastPage = 1;

            if (pagination.limit > 0 && pagination.offset > 0) {
                currentPage = Math.ceil((pagination.offset + 1) / pagination.limit);
            }

            if (pagination.limit > 0 && pagination.total > 0) {
                lastPage = Math.ceil(pagination.total / pagination.limit);
            }

            return {
                total: pagination.total,
                per_page: pagination.limit,
                current_page: currentPage,
                last_page: lastPage,
            };
        },

        formatData({ dispatch, state, commit }, data) {
            const contextId = state.listingname + '_' + new Date().getTime();
            const formatedColumns = state.listingMapping.columns.filter(function (column) {
                return typeof column.formatter === 'function';
            });

            return data.map(function (row) {
                formatedColumns.forEach(function (column) {
                    if (row[column.configuration.prop ?? column.name] !== null) {
                        row['formatted_' + column.name] = column.formatter(
                            row[column.configuration.prop ?? column.name],
                            row,
                        );
                    }
                });
                if (
                    state.listingMapping.contextMenus &&
                    state.listingMapping.contextMenus.length > 0
                ) {
                    row['contextmenu'] = {
                        items: state.listingMapping.contextMenus.map(function (menu) {
                            menu.contextId = contextId;
                            menu.permissions = state.permissions;
                            return menu.build(row);
                        }),
                    };
                }
                return row;
            });
        },

        formatKpis({ dispatch, state, commit }, data) {
            if (state.listingMapping.kpis && state.listingMapping.kpis.length > 0) {
                return state.listingMapping.kpis
                    .map(function (definition) {
                        if (data.aggregations.hasOwnProperty(definition.aggregation)) {
                            return definition.build(
                                data.aggregations[definition.aggregation],
                                data,
                            );
                        }
                        return null;
                    })
                    .filter((kpi) => {
                        return kpi !== null;
                    });
            }
            return null;
        },

        async formatResponse({ dispatch, state, commit }, data) {
            const formattedData = {};
            formattedData.meta = {};
            formattedData.meta.pagination = await dispatch('formatPagination', data.pagination);
            formattedData.data = await dispatch(
                'formatData',
                data.data,
                formattedData.meta.columns,
            );
            if (data.aggregations) {
                formattedData.kpis = await dispatch('formatKpis', data);
            } else {
                formattedData.kpis = state.kpis;
            }

            return formattedData;
        },

        refreshData({ dispatch, state, commit }, data) {
            commit('setupPagination', data.meta.pagination);
            commit('activePendingFilters');
            commit('updateKpiPresence', data.kpis.length > 0);
            commit('updateKpis', data.kpis);
            commit('flagInit');

            commit('updateRows', Object.keys(data.data).length > 0 ? data.data : {});

            dispatch('saveContext');
            dispatch('saveUserContext');
        },

        resetListing({ dispatch, state, commit }) {
            if (state.error) {
                commit('deleteError');
            }

            dispatch('loadColumns');
            dispatch('loadFilters');

            const sorting = state.listingMapping.defaultSort
                ? state.listingMapping.defaultSort
                : { field: '', direction: 'asc' };
            commit('updateSorting', sorting);
            commit('updateKpiPresence', false);
            commit('updateColumnsSize', []);

            dispatch('fetch', { current_page: 1 });
        },

        resetColumns({ dispatch, commit, state }) {
            dispatch('loadColumns');
            commit('updateColumnsSize', []);
        },

        resetFilters({ dispatch, commit, state }) {
            dispatch('loadFilters');
            dispatch('fetch', { current_page: 1 });
        },

        reloadKpis({ dispatch, state, commit }) {
            commit('updateKpiPresence', false);
            dispatch('fetch');
        },

        runExport({ commit, state, getters }, exportParams) {
            const params = {};

            if (!state.exportUrl) {
                throw new Error('Export url does not defined');
            }

            //Sorting
            if (state.sorting && state.sorting.field) {
                params.order = state.sorting.field;
                for (const column of state.columns) {
                    if (column.name == state.sorting.field) {
                        params.order = column.sortableKey;
                        break;
                    }
                }

                params.direction = state.sorting.direction.toLowerCase();
            }

            const data = {
                output_format: exportParams.outputFormat,
                charset: exportParams.charset ? exportParams.charset : 'UTF-8',
                columns: exportParams.allColumns
                    ? null
                    : getters.activeColumns.filter((col) => col.exportable).map((col) => col.name),
                filters: getters.formattedFilters,
            };

            if (typeof state.listingMapping.hooks.fetch === 'function') {
                state.listingMapping.hooks.fetch(data, state);
            }

            return new Promise((resolve, reject) => {
                ApiClient.post(state.exportUrl, params, data)
                    .then(() => {
                        resolve({ workerSent: true });
                    })
                    .catch(function (error) {
                        if (error.response && error.response.status == 423) {
                            resolve({ workerSent: false });
                        } else {
                            reject(error);
                        }
                    });
            });
        },

        getAllPages({ state, commit, getters }) {
            const data = {
                filters: getters.formattedFilters,
            };

            if (typeof state.listingMapping.hooks.fetch === 'function') {
                state.listingMapping.hooks.fetch(data, state);
            }

            const url = state.url;
            const fetchAllPages = async function (offset) {
                const params = {
                    offset: offset,
                };

                const response = await ApiClient.post(url, params, data);
                const pagination = response.data.pagination;
                const rows = response.data.data;

                if (pagination.count == pagination.limit) {
                    const offset = pagination.offset;
                    return await rows.concat(fetchAllPages(offset));
                }

                return rows;
            };

            return new Promise(async (resolve, reject) => {
                commit('setAjax', true);
                const rows = await fetchAllPages(null)
                    .then(function (rows) {
                        commit('setAjax', false);
                        resolve(rows);
                    })
                    .catch(reject);

                return rows;
            });
        },

        fetch({ dispatch, getters, state, commit }, overwriteMeta) {
            if (overwriteMeta === undefined) {
                overwriteMeta = {};
            }

            if (state.error) {
                commit('deleteError');
            }

            if (!!state.ajax) {
                typeof state.ajax === 'object' && typeof state.ajax.abort === 'function'
                    ? state.ajax.abort()
                    : null;
                commit('setAjax', false);
            }

            var params = {
                offset: 0,
                limit:
                    overwriteMeta.per_page === undefined
                        ? state.pagination.per_page
                        : overwriteMeta.per_page,
            };

            if (overwriteMeta.current_page && params.limit) {
                params.offset = params.limit * (overwriteMeta.current_page - 1);
            } else if (state.pagination.current_page && params.limit) {
                params.offset = params.limit * (state.pagination.current_page - 1);
            }

            if (getters.mustGetKpis && state.aggregations !== undefined) {
                const embed = [];
                state.aggregations.forEach((aggregation) => {
                    embed.push('aggregations.' + aggregation);
                });

                params.embed = embed;
            }

            if (state.sorting && state.sorting.field) {
                params.order = state.sorting.field;

                for (const column of state.columns) {
                    if (column.name == params.order && column.sortableKey) {
                        params.order = column.sortableKey;
                        break;
                    }
                }
                params.direction = state.sorting.direction.toLowerCase();
            }

            const data = {
                filters: getters.formattedFilters,
            };

            //Remove invalid filter
            for (const filterName in data.filters) {
                if (data.filters[filterName] === null) {
                    commit('removeFilter', { name: filterName });
                }
            }

            if (typeof state.listingMapping.hooks.fetch === 'function') {
                state.listingMapping.hooks.fetch(data, state);
            }

            var url = state.url;

            return new Promise((resolve, reject) => {
                commit(
                    'setAjax',
                    ApiClient.post(url, params, data)
                        .then(function (response) {
                            commit('setAjax', false);

                            if (response.data.pagination.offset > response.data.pagination.total) {
                                //Check if the current page exist
                                reject();
                                dispatch('fetch', { current_page: 1 });
                                return;
                            }

                            dispatch('formatResponse', response.data).then(function (data) {
                                dispatch('refreshData', data);
                                resolve(data);
                            });
                        })
                        .catch(function (err) {
                            commit('setAjax', false);
                            if (!ApiClient.isCancel(err)) {
                                commit('setError');
                            }
                            reject();
                        }),
                );
            });
        },
    },
};
